import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import {
  getMyTeam, regenerateInviteCode, setMemberRole, removeTeamMember, leaveTeam, deleteTeam,
  getInstallationSettingsUrl,
  createBillingCheckoutSession, createBillingPortalSession, confirmPlanSelection,
} from "../api";
import AppHeader from "../components/AppHeader";
import ConfirmDialog from "../components/ConfirmDialog";
import TeamlessPanel from "../components/TeamlessPanel";
import { getPlanStatusKind, getTrialDaysLeft, formatPeriodEnd } from "../utils/planStatus";

// Admin badge color — a distinct designation, not one of PrCard's priority
// colors or AgingPrTable's palette, so it can't be misread as a priority.
const ADMIN_BADGE_COLOR = "#a855f7";

function InviteCodeCard({ team, onRegenerate }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(team.invite_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="bg-rf-bg-secondary rounded-xl p-6 mb-6">
      <h2 className="text-base font-semibold text-rf-text-primary mb-1">Invite Teammates</h2>
      <p className="text-sm text-rf-text-secondary mb-4">
        Anyone with this code can join your team and see all of its PRs and reviewer data.
      </p>
      <code className="text-xs text-rf-accent-blue break-all block mb-4 bg-rf-bg-tertiary rounded-md p-3">
        {team.invite_code}
      </code>
      <div className="flex gap-2">
        <button
          onClick={handleCopy}
          className="px-4 py-2 rounded-md border border-rf-accent/30 bg-rf-accent/10 text-rf-accent text-sm font-medium hover:bg-rf-accent/20 transition-colors"
        >
          {copied ? "Copied!" : "Copy Code"}
        </button>
        <button
          onClick={onRegenerate}
          className="px-4 py-2 rounded-md border border-rf-border text-rf-text-secondary text-sm font-medium hover:text-rf-text-primary transition-colors"
        >
          Regenerate
        </button>
      </div>
    </div>
  );
}

function RoleBadge() {
  return (
    <span
      className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full"
      style={{ color: ADMIN_BADGE_COLOR, background: `${ADMIN_BADGE_COLOR}18` }}
    >
      Admin
    </span>
  );
}

function MemberRow({ member, isSelf, callerIsAdmin, onPromote, onDemote, onRemove }) {
  const initials = (member.display_name || member.github_username || "??")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();
  const isAdmin = member.team_role === "admin";

  return (
    <div className="flex items-center gap-3.5 py-3.5 border-b border-rf-border/40 last:border-0">
      <div className="w-10 h-10 rounded-full bg-rf-accent-blue/15 flex items-center justify-center text-xs text-rf-accent-blue font-semibold shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        {/*
          ⚠️ `truncate` goes on the NAME, never on this flex container. On the
          container it expands to overflow:hidden + ellipsis + nowrap, but
          text-overflow only applies to a block container's own inline content
          — the name is an anonymous FLEX ITEM here, so no ellipsis renders,
          the name won't shrink, and RoleBadge gets pushed out and clipped by
          the overflow:hidden. Same bug was live in TeamPanel.js.
        */}
        <div className="text-sm font-semibold text-rf-text-primary flex items-center gap-2 min-w-0">
          <span className="truncate">{member.display_name}</span>
          {isAdmin && <RoleBadge />}
        </div>
        <div className="text-[11px] text-rf-text-secondary font-mono">@{member.github_username}</div>
      </div>
      {callerIsAdmin && !isSelf && (
        <div className="flex gap-3 shrink-0">
          {isAdmin ? (
            <button
              onClick={() => onDemote(member)}
              className="text-xs text-rf-text-secondary hover:text-rf-text-primary transition-colors"
            >
              Demote
            </button>
          ) : (
            <button
              onClick={() => onPromote(member)}
              className="text-xs text-rf-text-secondary hover:text-rf-text-primary transition-colors"
            >
              Promote
            </button>
          )}
          <button
            onClick={() => onRemove(member)}
            className="text-xs text-rf-accent-danger hover:opacity-80 transition-opacity"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

// Item 9: status text is visible to every member; the upgrade/portal
// buttons and the plan-active picker are admin-only actions, gated by the
// caller separately from whether the status line itself renders.
function PlanBillingCard({ team, callerIsAdmin, onCheckout, onPortal, onConfirmSelection }) {
  const plan = team?.plan;
  const [billingInterval, setBillingInterval] = useState("monthly");
  const [selectedRepos, setSelectedRepos] = useState(() => new Set());
  const [selectedMembers, setSelectedMembers] = useState(() => new Set());

  // Found in review: without this, removing/promoting/demoting a member (or
  // confirming a selection) refetches `team` but the picker's own selection
  // state never resynced — a removed member's id lingered forever, silently
  // inflating the count and blocking a genuinely free slot. TeamPage has no
  // background polling/WS refresh of its own, so `team` only ever changes
  // here in response to a real action — resyncing on every change is safe
  // and doesn't fight an in-progress edit the user hasn't acted on yet.
  useEffect(() => {
    setSelectedRepos(new Set((team?.repos || []).filter((r) => r.plan_active).map((r) => r.repository_full_name)));
    setSelectedMembers(new Set((team?.members || []).filter((m) => m.plan_active).map((m) => m.user_id)));
  }, [team]);

  if (!plan) return null;

  const isFree = plan.effective_tier === "free";
  const repoLimit = plan.limits?.free_repo_limit ?? 1;
  const memberLimit = plan.limits?.free_member_limit ?? 5;
  // Bug found 2026-08-21 during real payment-flow testing, fixed twice the
  // same day. First cut: `plan.effective_tier === "free"` — meant a
  // trialing team (effective_tier is 'pro' via reason: 'trial') never saw
  // an Upgrade button, even though checkout has always been allowed during
  // a trial. Second cut (this one): gating on "no live Stripe subscription"
  // instead broke the opposite way — a *grandfathered* team (plan_tier
  // 'pro', backfilled before the paywall existed, with no trial_ends_at and
  // no stripe_subscription_id at all) has nothing to upgrade to and no
  // price to pay, but still got the nudge, since it has no live
  // subscription either. The actual intent is about plan.reason, not
  // subscription presence: only 'free' and 'trial' ever have a real reason
  // to see this button. 'paid'/'dunning_grace' use the Billing Portal
  // instead; 'grandfathered' needs neither.
  const showUpgrade = plan.reason === "free" || plan.reason === "trial";
  const showPortal = !!team?.stripe_subscription_id;

  const statusLine = () => {
    const kind = getPlanStatusKind(plan);
    if (kind === "trial") {
      const daysLeft = getTrialDaysLeft(plan);
      return `You're on a free trial of Pro — ${daysLeft} day${daysLeft === 1 ? "" : "s"} left, no card required.`;
    }
    if (kind === "dunning_grace") {
      return "Your last payment failed. You're still on Pro for a few more days while we retry — update your card to avoid losing access.";
    }
    // Item 24: a cancelled subscription keeps Pro until the period ends, so
    // the honest statement is "you still have it, and here's until when" —
    // not the bare "You're on Pro." that gave a just-cancelled user no
    // acknowledgement their cancellation had registered at all.
    if (kind === "pro_cancelling") {
      const ends = formatPeriodEnd(plan, { long: true });
      return ends
        ? `You're on Pro until ${ends}. Your subscription is cancelled and won't renew.`
        : "Your subscription is cancelled and won't renew. You keep Pro until the end of your current billing period.";
    }
    if (kind === "pro") return "You're on Pro.";
    return "You're on the Free plan.";
  };

  const toggleRepo = (fullName) => {
    setSelectedRepos((prev) => {
      const next = new Set(prev);
      if (next.has(fullName)) next.delete(fullName);
      else if (!isFree || next.size < repoLimit) next.add(fullName);
      return next;
    });
  };
  const toggleMember = (userId) => {
    setSelectedMembers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else if (!isFree || next.size < memberLimit) next.add(userId);
      return next;
    });
  };

  return (
    <div className="bg-rf-bg-secondary rounded-xl p-6 mb-6">
      <h2 className="text-base font-semibold text-rf-text-primary mb-1">Plan &amp; Billing</h2>
      <p className="text-sm text-rf-text-secondary mb-4">{statusLine()}</p>

      {plan.quota_flagged && (
        <div className="mb-4 p-4 rounded-lg bg-rf-accent-warning/10 border border-rf-accent-warning/30 text-rf-accent-warning text-sm">
          Your team has more connected repos or members than the Free plan allows ({repoLimit} repo, {memberLimit} members).
          {callerIsAdmin ? " Choose which stay active below, or upgrade to cover everything." : " Ask a team admin to resolve this."}
        </div>
      )}

      {callerIsAdmin && showUpgrade && (
        <div className="mb-5 p-4 rounded-lg bg-rf-bg-tertiary border border-rf-border">
          <div className="flex items-center gap-2 mb-3">
            <button
              type="button"
              onClick={() => setBillingInterval("monthly")}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                billingInterval === "monthly" ? "bg-rf-accent text-white" : "text-rf-text-secondary hover:text-rf-text-primary"
              }`}
            >
              Monthly — ${plan.pro_price_usd ?? 99}/mo
            </button>
            <button
              type="button"
              onClick={() => setBillingInterval("annual")}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                billingInterval === "annual" ? "bg-rf-accent text-white" : "text-rf-text-secondary hover:text-rf-text-primary"
              }`}
            >
              Annual — {plan.pro_annual_discount_pct ?? 20}% off
            </button>
          </div>
          <button
            onClick={() => onCheckout(billingInterval)}
            className="w-full py-2.5 rounded-lg bg-rf-accent text-white font-semibold text-sm hover:bg-rf-accent/90 transition-colors"
          >
            Upgrade to Pro →
          </button>
        </div>
      )}

      {callerIsAdmin && showPortal && (
        <button
          onClick={onPortal}
          className="px-4 py-2 rounded-md border border-rf-border text-rf-text-secondary text-sm font-medium hover:text-rf-text-primary transition-colors mb-2"
        >
          Manage billing
        </button>
      )}

      {callerIsAdmin && plan.quota_flagged && (
        <div className="border-t border-rf-border pt-4 mt-3">
          <h3 className="text-sm font-semibold text-rf-text-primary mb-3">Choose active repos and members</h3>

          <div className="mb-4">
            <div className="text-[11px] text-rf-text-muted uppercase tracking-wider mb-2">
              Repos ({selectedRepos.size}/{isFree ? repoLimit : "unlimited"})
            </div>
            {(team.repos || []).map((r) => (
              <label key={r.repository_full_name} className="flex items-center gap-2 py-1.5 text-sm text-rf-text-secondary">
                <input
                  type="checkbox"
                  checked={selectedRepos.has(r.repository_full_name)}
                  onChange={() => toggleRepo(r.repository_full_name)}
                  disabled={isFree && !selectedRepos.has(r.repository_full_name) && selectedRepos.size >= repoLimit}
                />
                <span className="font-mono">{r.repository_full_name}</span>
              </label>
            ))}
          </div>

          <div className="mb-4">
            <div className="text-[11px] text-rf-text-muted uppercase tracking-wider mb-2">
              Members ({selectedMembers.size}/{isFree ? memberLimit : "unlimited"})
            </div>
            {(team.members || []).map((m) => (
              <label key={m.user_id} className="flex items-center gap-2 py-1.5 text-sm text-rf-text-secondary">
                <input
                  type="checkbox"
                  checked={selectedMembers.has(m.user_id)}
                  onChange={() => toggleMember(m.user_id)}
                  disabled={isFree && !selectedMembers.has(m.user_id) && selectedMembers.size >= memberLimit}
                />
                <span>
                  {m.display_name} <span className="font-mono text-rf-text-muted">@{m.github_username}</span>
                </span>
              </label>
            ))}
          </div>

          {(selectedRepos.size === 0 || selectedMembers.size === 0) && (
            <p className="text-xs text-rf-accent-warning mb-2">
              Select at least one repo and one member — an empty selection would disable Anthurium for your team.
            </p>
          )}
          <button
            onClick={() => onConfirmSelection([...selectedRepos], [...selectedMembers])}
            disabled={selectedRepos.size === 0 || selectedMembers.size === 0}
            className="px-4 py-2 rounded-md bg-rf-accent text-white text-sm font-medium hover:bg-rf-accent/90 transition-colors disabled:opacity-40 disabled:pointer-events-none"
          >
            Confirm selection
          </button>
        </div>
      )}
    </div>
  );
}

function TeamPage() {
  const { user, hasTeam, logout, leaveCurrentTeam, deleteCurrentTeam } = useAuth();
  const [team, setTeam] = useState(null);
  const [pendingDeletion, setPendingDeletion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [confirmState, setConfirmState] = useState(null); // { type, member } | null
  // Team deletion gets its own dialog state rather than joining confirmState's
  // map, for two concrete reasons: it needs confirmationPhrase and busy, which
  // the shared confirmCopy entries don't carry, and runConfirmedAction clears
  // confirmState BEFORE awaiting — which would drop the dialog mid-request and
  // let a double-click fire two deletes. Same shape ProfilePage uses for
  // account deletion.
  const [confirmingDeleteTeam, setConfirmingDeleteTeam] = useState(false);
  const [deletingTeam, setDeletingTeam] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMyTeam();
      setPendingDeletion(res.data?.data?.pending_deletion || null);
      setTeam(res.data?.data?.team || null);
    } catch (err) {
      setError("Failed to load team: " + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Teamless used to <Navigate> back to /dashboard, which made the header's
  // Team link silently do nothing. It renders its own state now.
  //
  // Every real section is ABSENT rather than empty: no Members table with no
  // rows, no Invite Teammates card, no Plan & Billing, no danger zone. An
  // empty Members table would imply a team exists that happens to have nobody
  // in it — the same class of false claim as "Team unavailable" for someone
  // who simply has no team.
  if (hasTeam === false) {
    return (
      <div className="min-h-screen bg-rf-bg-primary">
        <AppHeader user={user} onLogout={logout} active="team" />
        <main className="max-w-[900px] mx-auto px-4 sm:px-8 py-7">
          <div className="mb-7">
            <h1 className="text-2xl font-bold text-rf-text-primary tracking-tight mb-1">Team</h1>
            <p className="text-sm text-rf-text-secondary">No team yet</p>
          </div>
          {/*
            Same two actions as the Dashboard's panel, different framing. The
            shared component owns the actions; the pages own only the copy. If
            these ever diverge between screens, that is a bug — one of them is
            then teaching a different way out than the other.
          */}
          <TeamlessPanel
            pendingDeletion={pendingDeletion}
            title="You're not on a team yet"
            body="A team is how Anthurium knows which repositories to watch and who is available to review. Starting one installs the GitHub App on an organization you choose; joining one needs only a code from a teammate."
          />
        </main>
      </div>
    );
  }

  const handleCheckout = async (interval) => {
    setActionError(null);
    try {
      const res = await createBillingCheckoutSession(interval);
      window.location.href = res.data?.data?.checkout_url;
    } catch (err) {
      setActionError(err.response?.data?.error || err.message);
    }
  };

  const handlePortal = async () => {
    setActionError(null);
    try {
      const res = await createBillingPortalSession();
      window.location.href = res.data?.data?.portal_url;
    } catch (err) {
      setActionError(err.response?.data?.error || err.message);
    }
  };

  const handleConfirmSelection = async (repoNames, memberIds) => {
    setActionError(null);
    try {
      await confirmPlanSelection(repoNames, memberIds);
      await fetchData();
    } catch (err) {
      setActionError(err.response?.data?.error || err.message);
    }
  };

  const handleRegenerate = async () => {
    try {
      const res = await regenerateInviteCode();
      const newCode = res.data?.data?.invite_code;
      setTeam((prev) => (prev ? { ...prev, invite_code: newCode } : prev));
    } catch (err) {
      setActionError(err.response?.data?.error || err.message);
    }
  };

  const handlePromote = (member) => {
    setActionError(null);
    setConfirmState({ type: "promote", member });
  };
  const handleDemote = (member) => {
    setActionError(null);
    setConfirmState({ type: "demote", member });
  };
  const handleRemove = (member) => {
    setActionError(null);
    setConfirmState({ type: "remove", member });
  };
  const handleLeave = () => {
    setActionError(null);
    setConfirmState({ type: "leave" });
  };

  const handleDeleteTeam = async () => {
    setActionError(null);
    setDeletingTeam(true);
    try {
      const res = await deleteTeam();
      const data = res.data?.data || {};
      // The dialog promised the App would be removed. If that step failed the
      // team is still deleted (the uninstall runs after COMMIT, deliberately —
      // primary intent survives a partial failure), so this is not an error
      // state. But staying silent would leave a broken promise, so carry the
      // fact forward and let the teamless dashboard offer the manual link.
      if (data.app_uninstalled === false && data.installation_id) {
        sessionStorage.setItem(
          "reviewflow_orphaned_installation",
          String(data.installation_id)
        );
      }
      // Not a hard reload, unlike account deletion: the session is still
      // valid, so a router navigate is correct and keeps the app warm.
      deleteCurrentTeam(data.session_token);
    } catch (err) {
      const status = err.response?.status;
      // 404 means the team is already gone — the delete committed and the
      // response was lost (Lambda timeout, dropped connection). From the
      // user's point of view the retry succeeded, so finish the flow instead
      // of showing an error for something that worked. Same handling
      // ProfilePage gives account deletion's 404.
      //
      // No fresh session token to adopt on this path, which is fine: the JWT's
      // team_id claim is never trusted — get-prs and ws-connect both resolve
      // it from a live users lookup (gotcha #22).
      if (status === 404) {
        deleteCurrentTeam();
        return;
      }
      setDeletingTeam(false);
      setConfirmingDeleteTeam(false);
      // Always prefer the SERVER's message. These were briefly hardcoded per
      // status here, which was a real bug and its own test caught it: the
      // server distinguishes a case the client cannot see — a 409 where the
      // subscription had ALREADY been cancelled before the conflict, because
      // Stripe is called before the transaction and can't be rolled back with
      // it. A hardcoded "nothing was deleted" flatly contradicts that.
      //
      // The server's own copy is written for the user in every branch here
      // (409, 502, 400), so there is nothing to improve on locally and a
      // second copy would only drift.
      setActionError(err.response?.data?.error || err.message);
    }
  };

  const runConfirmedAction = async () => {
    if (!confirmState) return;
    const { type, member } = confirmState;
    setConfirmState(null);
    try {
      if (type === "promote") {
        await setMemberRole(member.user_id, "admin");
        await fetchData();
      } else if (type === "demote") {
        await setMemberRole(member.user_id, "member");
        await fetchData();
      } else if (type === "remove") {
        await removeTeamMember(member.user_id);
        await fetchData();
      } else if (type === "leave") {
        const res = await leaveTeam();
        leaveCurrentTeam(res.data?.data?.session_token);
      }
    } catch (err) {
      setActionError(err.response?.data?.error || err.message);
    }
  };

  if (loading && !team) {
    return (
      <div className="min-h-screen bg-rf-bg-primary flex items-center justify-center">
        <div className="text-rf-text-secondary text-sm">Loading team...</div>
      </div>
    );
  }

  const members = team?.members || [];
  const callerIsAdmin = !!team?.is_admin;

  const confirmCopy = {
    promote: {
      title: "Promote to admin",
      message: `Make ${confirmState?.member?.display_name} an admin? They'll be able to manage roles, remove teammates, and regenerate the invite code.`,
      confirmLabel: "Promote",
      danger: false,
    },
    demote: {
      title: "Demote to member",
      message: `Remove admin access from ${confirmState?.member?.display_name}?`,
      confirmLabel: "Demote",
      danger: true,
    },
    remove: {
      title: "Remove teammate",
      message: `Remove ${confirmState?.member?.display_name} from the team? Any PRs assigned to them will be reassigned to another reviewer.`,
      confirmLabel: "Remove",
      danger: true,
    },
    leave: {
      title: "Leave team",
      message: "You'll lose access to this team's PRs and reviewer data. You can rejoin later with a new invite code.",
      confirmLabel: "Leave Team",
      danger: true,
    },
  }[confirmState?.type];

  return (
    <div className="min-h-screen bg-rf-bg-primary">
      <AppHeader user={user} onLogout={logout} plan={team?.plan} active="team" />

      <main className="max-w-[1200px] mx-auto px-8 py-7">
        <div className="mb-7">
          <h1 className="text-2xl font-bold text-rf-text-primary tracking-tight mb-1">Team</h1>
          <p className="text-sm text-rf-text-secondary">
            {team?.team_name || "Team"} · {members.length} member{members.length !== 1 ? "s" : ""}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-lg bg-rf-accent-danger/10 border border-rf-accent-danger/30 text-rf-accent-danger text-sm">
            {error}
            <button onClick={fetchData} className="ml-3 underline hover:no-underline">
              Retry
            </button>
          </div>
        )}
        {actionError && (
          <div className="mb-6 p-4 rounded-lg bg-rf-accent-danger/10 border border-rf-accent-danger/30 text-rf-accent-danger text-sm">
            {actionError}
          </div>
        )}

        <PlanBillingCard
          team={team}
          callerIsAdmin={callerIsAdmin}
          onCheckout={handleCheckout}
          onPortal={handlePortal}
          onConfirmSelection={handleConfirmSelection}
        />

        {callerIsAdmin && team?.invite_code && (
          <InviteCodeCard team={team} onRegenerate={handleRegenerate} />
        )}

        <div className="bg-rf-bg-secondary rounded-xl p-6 mb-6">
          <h2 className="text-base font-semibold text-rf-text-primary mb-2">Members</h2>
          <div>
            {members.map((m) => (
              <MemberRow
                key={m.user_id}
                member={m}
                isSelf={m.user_id === user?.user_id}
                callerIsAdmin={callerIsAdmin}
                onPromote={handlePromote}
                onDemote={handleDemote}
                onRemove={handleRemove}
              />
            ))}
          </div>
        </div>

        <div className="bg-rf-bg-secondary rounded-xl p-6">
          {/*
            Heading covers both exits, because which one this panel offers
            depends on membership — and it renders ABOVE the `!team` guard
            below, so it's also on screen while membership is unknown. "Leave
            Team" as a fixed heading was fine when leaving was the only action
            here; it would now mislabel the sole-member branch, which offers
            deletion instead.

            The old subtitle ("You'll lose access to this team's dashboard,
            PRs, and reviewer data") moved into the leave branch. It describes
            losing *access*, which is true of leaving and false of deleting —
            deletion destroys the data rather than putting it out of reach —
            and it was another unsupportable claim while team is null.
          */}
          <h2 className="text-base font-semibold text-rf-text-primary mb-4">Leave or delete this team</h2>
          {/*
            Gates on members.length ALONE. It used to also require
            github_installation_id — which is the exact field uninstalling sets
            to NULL, so following this panel's own advice flipped the condition,
            swapped the guidance for a live "Leave Team" button, and handed the
            user the server's rejection instead (found live 2026-08-28). The
            guard disabled itself at precisely the moment it was still needed.

            The install link is now nested rather than gating, because it's the
            only part that genuinely needs an installation id to point at.

            `<= 1`, not `=== 1`, so this fails CLOSED. `members` is
            `team?.members || []`, and there is no early return on fetch error
            — so if getMyTeam() fails while the JWT still says hasTeam, the
            component renders with an empty members array. Exact equality would
            be false there and reveal the destructive button, which is the same
            failure shape this whole fix exists to remove: a condition false for
            an unintended reason. When membership is unknown, hide the action.
          */}
          {/*
            `!team` first, so nothing in this panel renders while the team is
            unknown. Without it a failed getMyTeam() leaves members = [] and the
            copy below states "You're the only member, so there's no one to hand
            this team to" — a claim we cannot support, printed next to a
            "Failed to load team" error and a "0 members" header. Hiding the
            Leave button was already handled by `<= 1`; asserting a membership
            fact was not.
          */}
          {!team ? null : members.length <= 1 ? (
            <div className="flex flex-col items-start gap-3">
              {/*
                The slot keeps its place when the App is already gone rather
                than disappearing — an element vanishing reads as a rendering
                bug, and the absence is itself the thing worth reporting.

                `team` is guaranteed non-null here — the outer guard above
                handles unknown — so two branches are correct at this level.
                Do not reintroduce a `team?.` here: optional chaining would
                imply a third state that can't reach this point, and reading it
                as "false means uninstalled" is the exact defect that shipped on
                the Dashboard chip one PR earlier (CLAUDE.md gotcha #44).

                The status is a <span>, not a link: reinstalling is offered by
                the Dashboard banner, and this panel is about leaving the team.
                It deliberately reuses the link's own classes minus `hover:` and
                `transition-colors` — same shape and weight so the slot doesn't
                shift, but no hover affordance, since nothing here is clickable.
              */}
              {team.github_installation_id ? (
                <a
                  href={getInstallationSettingsUrl(team.github_installation_id)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block px-4 py-2 rounded-md border border-rf-border text-rf-text-secondary text-sm font-medium hover:text-rf-text-primary transition-colors"
                >
                  Uninstall the GitHub App →
                </a>
              ) : (
                <span className="inline-block px-4 py-2 rounded-md border border-rf-border text-rf-text-secondary text-sm font-medium">
                  GitHub App uninstalled
                </span>
              )}
              {/*
                Tense follows the slot above: reading "uninstalling … discon-
                nects your repositories" in the future tense directly beneath a
                badge saying it already happened is jarring, and makes the badge
                look like it hasn't registered.
              */}
              {/*
                rf-text-secondary, not rf-text-muted: #4a5568 measures 2.27:1
                on this card against WCAG AA's 4.5:1 (gotcha #49), and this is
                the copy explaining an irreversible action.
              */}
              <p className="text-xs leading-relaxed text-rf-text-secondary max-w-[62ch]">
                You're the only member, so there's no one to hand this team to.{" "}
                {team.github_installation_id
                  ? "Uninstalling the GitHub App disconnects your repositories but keeps the team and your account."
                  : "Uninstalling the App disconnected your repositories, but the team and your account are still here."}{" "}
                To remove the team itself, delete it below — your profile stays,
                and you can join or start another team afterwards.
              </p>
              {/*
                This used to point at "delete your account" on /profile, which
                was then the only exit and erases the person along with the
                team. DELETE /api/teams/me is now the smaller action, so
                sending someone to account deletion would overstate what's
                required to get rid of a team.
              */}
              <button
                onClick={() => { setActionError(null); setConfirmingDeleteTeam(true); }}
                className="px-4 py-2 rounded-md border border-rf-accent-danger/30 bg-rf-accent-danger/10 text-rf-accent-danger text-sm font-medium hover:bg-rf-accent-danger/20 transition-colors"
              >
                Delete Team
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm text-rf-text-secondary mb-4">
                You'll lose access to this team's dashboard, PRs, and reviewer
                data. The team itself stays, along with everything in it.
              </p>
              <button
                onClick={handleLeave}
                className="px-4 py-2 rounded-md border border-rf-accent-danger/30 bg-rf-accent-danger/10 text-rf-accent-danger text-sm font-medium hover:bg-rf-accent-danger/20 transition-colors"
              >
                Leave Team
              </button>
            </>
          )}
        </div>
      </main>

      <ConfirmDialog
        open={!!confirmState}
        title={confirmCopy?.title}
        message={confirmCopy?.message}
        confirmLabel={confirmCopy?.confirmLabel}
        danger={confirmCopy?.danger}
        onConfirm={runConfirmedAction}
        onCancel={() => setConfirmState(null)}
      />

      {/*
        `&& !!team` fails closed. Every claim in this dialog reads off `team`,
        including confirmationPhrase — and a null team_name would make
        ConfirmDialog treat the phrase as absent, silently REMOVING the
        type-to-confirm gate from an irreversible action while the title read
        "Delete undefined?". Not reachable today (nothing refetches while the
        modal is open), but this is the guard, not the reachability.
      */}
      <ConfirmDialog
        open={confirmingDeleteTeam && !!team}
        danger
        busy={deletingTeam}
        busyLabel="Deleting…"
        title={`Delete ${team?.team_name}?`}
        // Type-to-confirm, matching the bar ProfilePage sets for the
        // equivalent cascade — this destroys a team's entire history and can
        // cancel a paid subscription.
        confirmationPhrase={team?.team_name}
        confirmLabel="Delete team"
        message={
          <>
            <p className="mb-3">
              This schedules {team?.team_name} for deletion — pull request history, review
              assignments, repository connections and analytics. Nothing is removed straight away:
              you have <span className="text-rf-text-primary">30 days</span> to change your mind
              and restore it, after which it is permanently deleted.
            </p>
            <p className="mb-3">
              <span className="text-rf-text-primary">Your profile stays.</span> You'll land on an
              empty dashboard where you can join another team or start a new one.
            </p>
            {/*
              Only claimed when there's actually something to cancel, and it
              names the date rather than saying "immediately" abstractly. The
              backend cancels outright rather than at period end — it's
              deleting the record that would let it honour the rest of the
              period — so this must not imply the remaining time is kept.
            */}
            {team?.stripe_subscription_id && team?.plan?.subscription_status !== "canceled" && (
              <p className="mb-3 text-rf-accent-warning">
                Your subscription will be cancelled immediately
                {formatPeriodEnd(team.plan, { long: true })
                  ? `, ending the period you've paid through ${formatPeriodEnd(team.plan, { long: true })}`
                  : ""}
                . Partial periods aren't refunded.
              </p>
            )}
            {/*
              We cannot uninstall the App ourselves — no API exists for an App
              to remove its own installation (gotcha #37) — so claiming the
              repos are disconnected would be false. It keeps sending webhooks,
              which land as a handled no-op.

              ⚠️ THAT GOTCHA WAS WRONG and this copy said so to customers.
              DELETE /app/installations/{id} exists and authenticates with the
              App JWT we already mint. Deleting a team now removes the App as
              part of the same action, so the sentence is inverted — and it
              MUST be stated, because it is a second irreversible consequence
              that the type-to-confirm only nominally covers.
            */}
            {team?.github_installation_id && (
              <p className="text-rf-accent-warning">
                Anthurium will also be removed from your GitHub organization. Reconnecting later
                means installing it again and re-selecting repositories.
              </p>
            )}
          </>
        }
        onConfirm={handleDeleteTeam}
        onCancel={() => setConfirmingDeleteTeam(false)}
      />
    </div>
  );
}

export default TeamPage;
